import React, { useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { Collapse, Grid, IconButton, Stack, Typography } from '@mui/material'
import loadComponent from 'src/utils/loadMFE'
import { spacing } from 'src/utils/styled'
import { useNavigate } from 'react-router-dom'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Training, Event, Workflow } from 'types/traiwiss'
import RouteContainer from 'src/components/App/RouteContainer'
import { Certificate } from 'types/certification'
import { getCertificationUrl } from 'src/utils/env'
import { useFlightDeck } from 'src/context/FlightDeckContext'

const EventParticipationDashboard = React.lazy(() => loadComponent('traiwiss', 'ParticipationDashboard'))
const ParticipationProgressCard = React.lazy(() => loadComponent('traiwiss', 'ParticipationProgressCard'))
const HighlightEventCard = React.lazy(() => loadComponent('traiwiss', 'HighlightEventCard'))
const WelcomeCard = React.lazy(() => loadComponent('orga', 'WelcomeCard'))
const MyCertificateList = React.lazy(() => loadComponent('certification', 'MyCertificateList'))
const MyPendingCertificatesAlert = React.lazy(() => loadComponent('certification', 'MyPendingCertificatesAlert'))

const StyledStack = styled(Stack)`
    margin-top: ${spacing(2)};
    flex: 1;
    max-width: 100%;

    max-height: calc(
        100vh - ${spacing(8)} - ${spacing(4)} - ${spacing(4)} - ${spacing(6)}
    ); // Spacings TopBar, Breadcrumb, margin-top, FooterSpacer
`

// const StyledScrollBox = styled(Box)`
//     overflow: auto;
//     flex-grow: 1;
// `

const StyledHeading = styled(Typography)`
    margin-bottom: ${({ theme }) => theme.spacing(1)};
`

const StyledCollapse = styled(Collapse)`
    flex: 0 0 auto;
`

const StyledBox = styled(Stack)`
    margin-top: ${spacing(0)};
`

export function EventParticipationDashboardRoute() {
    const { academy } = useFlightDeck()

    const [workflowFullscreen, setWorkflowFullscreen] = useState(false)

    const navigate = useNavigate()
    const certificatesHeadingRef = useRef<HTMLElement>(null)

    const handleGotoEvent = (event: Event) => {
        navigate('/trainings/' + event.training._id)
    }

    const handlePrimaryButtonClicked = () => {
        navigate('/events/calendar/participate')
    }

    const handleGotoTraining = (training: Training) => {
        navigate('/trainings/' + training._id)
    }

    const handleGotoWorkflow = (workflow: Workflow) => {
        navigate('/workflows/' + workflow._id)
    }

    const handleStartCertificate = (certificate: Certificate) => {
        window.open(`${getCertificationUrl()}/certificate/${certificate._id}`, '_blank')
    }

    const handleScrollToCertificates = () => {
        setWorkflowFullscreen(true)
        certificatesHeadingRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const handleGotoPlayback = (playbackId: string) => {
        navigate('/playback/' + playbackId)
    }

    return (
        <RouteContainer wide>
            <StyledStack direction="column">
                <StyledCollapse in={!workflowFullscreen}>
                    <MyPendingCertificatesAlert onNavigateToCertificates={handleScrollToCertificates} />
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} lg={6}>
                            <WelcomeCard primaryButtonText="Trainingskalender öffnen" onPrimaryButtonClicked={handlePrimaryButtonClicked} />
                        </Grid>
                        <Grid item xs={6} lg={3}>
                            <HighlightEventCard gotoEvent={handleGotoEvent} />
                        </Grid>
                        <Grid item xs={6} lg={3}>
                            <ParticipationProgressCard />
                        </Grid>
                    </Grid>
                </StyledCollapse>
                <StyledBox direction="row" spacing={1}>
                    <StyledHeading variant="h6">Meine Workflows</StyledHeading>
                    <IconButton onClick={() => setWorkflowFullscreen((oldState) => !oldState)} size="small">
                        {workflowFullscreen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                </StyledBox>
                <StyledBox>
                    <EventParticipationDashboard onGotoTraining={handleGotoTraining} onGotoWorkflow={handleGotoWorkflow} onGotoPlayback={handleGotoPlayback} academyId={academy?._id} />
                </StyledBox>
                <StyledHeading ref={certificatesHeadingRef} variant="h6" sx={{ mt: 4 }}>
                    Meine Zertifikate
                </StyledHeading>
                <StyledBox>
                    <MyCertificateList onStartCertificate={handleStartCertificate} onRetryCertificate={handleStartCertificate} />
                </StyledBox>
            </StyledStack>
        </RouteContainer>
    )
}
